import React from 'react';
import {flatten} from 'lodash';

import type {
  NormalizedAtomConfig,
  CustomAtomConfigShape,
} from '@packages/systems/wfdl';
import {
  Plugins,
  withSystem,
  ElementPreset,
  Component,
  TypeImplementation,
  Atom,
} from '@packages/systems/wfdl';
import {BuiltinFactoryTest} from '@packages/systems/wfdl-builtin-plugin';
import {
  iconName,
  qualify,
  QualifiedMap,
} from '@packages/systems/wfdl-Qualified';

import {
  TList,
  TRecord,
  EElement,
  EText,
  ERecord,
  EEnum,
  EList,
  type Expression,
  TElement,
  TText,
} from '@packages/systems/wfdl-ast';
import {EvaluatedNodeData} from '@packages/utilities/data-utils';

const Builtin = BuiltinFactoryTest();

const pluginTest = Plugins.name('pluginTest');

const AtomTestRender = Atom.createRender(
  ({attributes, classes, data, generateAttributes, innerHTML, renderChild}) => (
    <div {...generateAttributes(attributes, classes)}>
      {
        // TODO (platform): fix innerHTML type issues

        innerHTML ||
          // @ts-expect-error - TS18046 - 'data' is of type 'unknown'
          data?.children?.map((child: EvaluatedNodeData) => renderChild(child))
      }
    </div>
  )
);

/**
 * Create a `ComponentMap` containing a page with the specified expression as
 * its `render` function. The page will be inserted into the `ComponentMap` as
 * `__SitePlugin::Page`.
 *
 * You may also provide a plain (non-`Qualified`) map of `Component`s which will
 * also inserted into `__SitePlugin` - use this to simulate any "userland"
 * components (symbols) that need to be referenced in your required page
 * `render` expression.
 *
 * Lastly, you may provide an existing `Plugins` structure and any `Component`s
 * that already exist there will be merged with the final `ComponentMap`.
 */
export const createComponentMapForTest = (
  render?: Expression,
  components?: {
    [key: string]: Component.ComponentType;
  },
  plugins?: Plugins.PluginsType
): Component.ComponentMap => {
  let componentMap: Component.ComponentMap = QualifiedMap.empty();
  if (render) {
    componentMap = componentMap.set(
      qualify(Plugins.name('__SitePlugin'), Component.name('page')),
      Component.create({
        render,
        displayName: 'Page',
        icon: qualify(Plugins.name('Basic'), iconName('Page')),
      })
    );
  }
  if (components) {
    Object.keys(components).forEach((componentId) => {
      componentMap = componentMap.set(
        qualify(Plugins.name('__SitePlugin'), Component.name(componentId)),
        // @ts-expect-error - TS2345 - Argument of type 'InternalComponent | undefined' is not assignable to parameter of type 'InternalComponent'.
        components[componentId]
      );
    });
  }
  return plugins
    ? componentMap.union(Plugins.getPluginComponents(plugins))
    : componentMap;
};

export const createComponentForTest = Component.create;

const aliasTypeDefaults: TypeImplementation.AliasType = {
  type: TText,
  render: () => null,
};

export const createAliasTypeForTest = (
  typeVal?: Partial<TypeImplementation.AliasType>
) =>
  TypeImplementation.Alias({
    ...aliasTypeDefaults,
    ...typeVal,
  });

const atomConfigDefaults = {
  displayName: 'Test Atom',
  showEmpty: false,
  render: AtomTestRender,
  dataType: TRecord({
    children: withSystem({isBindable: true, label: 'Text'}, TList(TElement)),
  }),
} as const;

export const createAtomForTest = (config?: Partial<CustomAtomConfigShape>) =>
  Atom.create({
    ...atomConfigDefaults,
    ...config,
  });

const atomConfigNormalized: NormalizedAtomConfig = {
  ...atomConfigDefaults,
  atomKey: '',
  key: '',
  pluginKey: '',
  stateAtom: '',
  constraints: {
    wraps: [],
    pinToParent: false,
    ancestors: [],
    parent: [],
    descendants: [],
    children: [],
    page: [],
  },
  // @ts-expect-error - Flow 0.121.0
  icon: {
    type: 'Component',
  },
  pathToReplaceableString: ['value'],
};

export const createNormalizedAtomForTest = (
  config?: Partial<
    NormalizedAtomConfig & {
      constraints: Partial<NormalizedAtomConfig['constraints']>;
    }
  >
) => ({
  ...atomConfigNormalized,
  ...config,
});

export const createOpaquePluginsForTest = (plugins?: {
  [key: string]: Plugins.PluginDeclarations;
}): Plugins.PluginsType => {
  if (plugins === undefined) {
    plugins = {
      pluginTest: Plugins.PrivilegedPlugin({
        icons: {TestIcon: () => ''},
        atoms: {TestAtom: createAtomForTest()},
        elementPresets: {
          TestAtom: ElementPreset.create({
            factory: EElement({
              id: 'Test Atom',
              type: qualify(pluginTest, Component.name('TestAtom')),
              data: ERecord({
                tag: EEnum('div'),
                children: EList([
                  EElement({
                    id: 'Heading',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Heading')
                    ),
                    data: ERecord({
                      tag: EText('h1'),
                      children: EList([
                        EElement({
                          id: 'String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Heading'),
                        }),
                      ]),
                    }),
                  }),
                  EElement({
                    id: 'Paragraph',
                    type: qualify(
                      Plugins.name('Basic'),
                      Component.name('Paragraph')
                    ),
                    data: ERecord({
                      children: EList([
                        EElement({
                          id: 'String',
                          type: qualify(
                            Plugins.name('Basic'),
                            Component.name('String')
                          ),
                          data: EText('Some paragraph text'),
                        }),
                      ]),
                    }),
                  }),
                ]),
              }),
            }),
            label: 'Test Atom',
            icon: {
              medium: qualify(pluginTest, iconName('TestIcon')),
              small: qualify(pluginTest, iconName('TestIcon')),
            },
          }),
          TestEscapeHatchExpression: ElementPreset.create({
            factory: ({collectionState}) => {
              if (collectionState === undefined) {
                throw Error(
                  'collectionState is required to instantiate this elementPreset'
                );
              }
              return EElement({
                id: 'Test Atom',
                type: qualify(pluginTest, Component.name('TestAtom')),
                data: ERecord({
                  tag: EEnum('div'),
                  children: EList(
                    flatten(
                      collectionState.items.toArray().map((category) => {
                        // @ts-expect-error TS2339: Property name does not exist on type ItemRecordType
                        const name = category.name;
                        // @ts-expect-error TS2339: Property slug does not exist on type ItemRecordType
                        const slug = category.slug;

                        return [
                          EElement({
                            id: 'Heading',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Heading')
                            ),
                            data: ERecord({
                              tag: EText('h1'),
                              children: EList([
                                EElement({
                                  id: 'String',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('String')
                                  ),
                                  data: EText(name),
                                }),
                              ]),
                            }),
                          }),
                          EElement({
                            id: 'Paragraph',
                            type: qualify(
                              Plugins.name('Basic'),
                              Component.name('Paragraph')
                            ),
                            data: ERecord({
                              children: EList([
                                EElement({
                                  id: 'String',
                                  type: qualify(
                                    Plugins.name('Basic'),
                                    Component.name('String')
                                  ),
                                  data: EText(slug),
                                }),
                              ]),
                            }),
                          }),
                        ];
                      })
                    )
                  ),
                }),
              });
            },
            label: 'Test Atom',
            icon: {
              medium: qualify(pluginTest, iconName('TestIcon')),
              small: qualify(pluginTest, iconName('TestIcon')),
            },
          }),
        },
      }),
    };
  }
  return Plugins.bootstrap({...plugins, Builtin});
};

export const createPluginsForTest = (plugins?: {
  [key: string]: Plugins.PluginDeclarations;
}): Plugins.PluginsType => {
  return createOpaquePluginsForTest(plugins);
};
